// ============================================
// 关键帧动画引擎
// ============================================

export type InterpolationType = 'linear' | 'easeIn' | 'easeOut' | 'easeInOut' | 'step';

export interface Keyframe {
  id: string;
  time: number; // 0-1 normalized
  value: number[];
  interpolation: InterpolationType;
}

export interface AnimationTrack {
  id: string;
  name: string;
  targetId: string; // node or model id
  property: string; // 'position.x', 'rotation.y', 'scale', 'opacity', etc.
  keyframes: Keyframe[];
  enabled: boolean;
}

export interface AnimationClip {
  id: string;
  name: string;
  duration: number; // seconds
  tracks: AnimationTrack[];
  loop: boolean;
  speed: number;
}

export interface AnimationState {
  clip: AnimationClip | null;
  isPlaying: boolean;
  currentTime: number; // seconds
  progress: number; // 0-1
}

// Interpolation functions
const interpolations: Record<InterpolationType, (t: number) => number> = {
  linear: (t) => t,
  easeIn: (t) => t * t,
  easeOut: (t) => t * (2 - t),
  easeInOut: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  step: (t) => (t < 1 ? 0 : 1),
};

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpArray(a: number[], b: number[], t: number): number[] {
  const len = Math.min(a.length, b.length);
  const result: number[] = [];
  for (let i = 0; i < len; i++) {
    result.push(lerp(a[i], b[i], t));
  }
  return result;
}

export class KeyframeEngine {
  private state: AnimationState = {
    clip: null,
    isPlaying: false,
    currentTime: 0,
    progress: 0,
  };
  private listeners: ((state: AnimationState) => void)[] = [];
  private animFrame: number | null = null;
  private lastTime: number = 0;

  getState(): AnimationState {
    return { ...this.state };
  }

  loadClip(clip: AnimationClip): void {
    this.stop();
    this.state.clip = clip;
    this.state.currentTime = 0;
    this.state.progress = 0;
    this.notify();
  }

  play(): void {
    if (!this.state.clip || this.state.isPlaying) return;
    this.state.isPlaying = true;
    this.lastTime = performance.now() / 1000;
    this.tick();
    this.notify();
  }

  pause(): void {
    this.state.isPlaying = false;
    if (this.animFrame !== null) {
      cancelAnimationFrame(this.animFrame);
      this.animFrame = null;
    }
    this.notify();
  }

  stop(): void {
    this.pause();
    this.state.currentTime = 0;
    this.state.progress = 0;
    this.notify();
  }

  seek(time: number): void {
    if (!this.state.clip) return;
    this.state.currentTime = Math.max(0, Math.min(time, this.state.clip.duration));
    this.state.progress = this.state.currentTime / this.state.clip.duration;
    this.notify();
  }

  seekProgress(progress: number): void {
    if (!this.state.clip) return;
    this.seek(progress * this.state.clip.duration);
  }

  setSpeed(speed: number): void {
    if (this.state.clip) {
      this.state.clip.speed = speed;
    }
  }

  addTrack(track: AnimationTrack): void {
    if (!this.state.clip) return;
    this.state.clip.tracks.push(track);
  }

  removeTrack(trackId: string): void {
    if (!this.state.clip) return;
    this.state.clip.tracks = this.state.clip.tracks.filter(t => t.id !== trackId);
  }

  addKeyframe(trackId: string, keyframe: Keyframe): void {
    if (!this.state.clip) return;
    const track = this.state.clip.tracks.find(t => t.id === trackId);
    if (!track) return;
    track.keyframes.push(keyframe);
    track.keyframes.sort((a, b) => a.time - b.time);
  }

  removeKeyframe(trackId: string, keyframeId: string): void {
    if (!this.state.clip) return;
    const track = this.state.clip.tracks.find(t => t.id === trackId);
    if (!track) return;
    track.keyframes = track.keyframes.filter(k => k.id !== keyframeId);
  }

  updateKeyframe(trackId: string, keyframeId: string, updates: Partial<Keyframe>): void {
    if (!this.state.clip) return;
    const track = this.state.clip.tracks.find(t => t.id === trackId);
    if (!track) return;
    const kf = track.keyframes.find(k => k.id === keyframeId);
    if (!kf) return;
    Object.assign(kf, updates);
    track.keyframes.sort((a, b) => a.time - b.time);
  }

  evaluate(track: AnimationTrack, time: number): number[] | null {
    const kfs = track.keyframes;
    if (kfs.length === 0) return null;
    if (kfs.length === 1) return [...kfs[0].value];

    const normalizedTime = time / (this.state.clip?.duration || 1);
    const clampedTime = Math.max(0, Math.min(1, normalizedTime));

    // Find surrounding keyframes
    let left = kfs[0];
    let right = kfs[kfs.length - 1];

    for (let i = 0; i < kfs.length - 1; i++) {
      if (clampedTime >= kfs[i].time && clampedTime <= kfs[i + 1].time) {
        left = kfs[i];
        right = kfs[i + 1];
        break;
      }
    }

    if (clampedTime <= left.time) return [...left.value];
    if (clampedTime >= right.time) return [...right.value];

    const duration = right.time - left.time;
    const t = duration > 0 ? (clampedTime - left.time) / duration : 0;
    const interp = interpolations[right.interpolation](t);

    return lerpArray(left.value, right.value, interp);
  }

  evaluateAll(): Map<string, Map<string, number[]>> {
    const result = new Map<string, Map<string, number[]>>();
    if (!this.state.clip) return result;

    for (const track of this.state.clip.tracks) {
      if (!track.enabled) continue;
      const value = this.evaluate(track, this.state.currentTime);
      if (value) {
        if (!result.has(track.targetId)) {
          result.set(track.targetId, new Map());
        }
        result.get(track.targetId)!.set(track.property, value);
      }
    }

    return result;
  }

  subscribe(listener: (state: AnimationState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }

  private tick = (): void => {
    if (!this.state.isPlaying || !this.state.clip) return;

    const now = performance.now() / 1000;
    const delta = now - this.lastTime;
    this.lastTime = now;

    this.state.currentTime += delta * this.state.clip.speed;
    this.state.progress = this.state.currentTime / this.state.clip.duration;

    if (this.state.currentTime >= this.state.clip.duration) {
      if (this.state.clip.loop) {
        this.state.currentTime = this.state.currentTime % this.state.clip.duration;
      } else {
        this.state.currentTime = this.state.clip.duration;
        this.state.isPlaying = false;
        this.notify();
        return;
      }
    }

    this.notify();
    this.animFrame = requestAnimationFrame(this.tick);
  };

  private notify(): void {
    for (const listener of this.listeners) {
      listener(this.getState());
    }
  }

  destroy(): void {
    this.stop();
    this.listeners = [];
  }
}

export function createDefaultClip(name: string, duration: number = 5): AnimationClip {
  return {
    id: crypto.randomUUID(),
    name,
    duration,
    tracks: [],
    loop: false,
    speed: 1,
  };
}

export function createTrack(
  name: string,
  targetId: string,
  property: string,
  keyframes: Keyframe[] = []
): AnimationTrack {
  return {
    id: crypto.randomUUID(),
    name,
    targetId,
    property,
    keyframes,
    enabled: true,
  };
}

export function createKeyframe(
  time: number,
  value: number[],
  interpolation: InterpolationType = 'linear'
): Keyframe {
  return {
    id: crypto.randomUUID(),
    time,
    value,
    interpolation,
  };
}
